/**
 * dashboardPreferences.js
 *
 * Per-browser dashboard preferences persisted to localStorage. A single versioned,
 * namespaced JSON object under `ava.dashboard.preferences.v1` holds every dashboard-scoped
 * preference, so future prefs share one key instead of each scattering its own. Preferences
 * are browser-local - never synced to the account: e.g. `autoOpenViewer` decides whether
 * selecting a chart filter opens the 3D viewer. Reads and writes are total - a missing,
 * unavailable, or corrupt store yields the registered default and nothing here throws (mirrors
 * SqlViewDataGrid's column-state persistence).
 *
 * PURE module - no Vuex, no components, no other project imports.
 */

// Versioned (v1 lets a future breaking shape bump the key without colliding with old data)
// and namespaced under `ava.dashboard.` alongside the app's other browser-local keys.
const STORAGE_KEY = 'ava.dashboard.preferences.v1';

// Exported for `storage`-event listeners (cross-tab preference sync): other same-origin tabs
// receive storage events keyed by this value whenever a preference is written here.
export const DASHBOARD_PREFS_STORAGE_KEY = STORAGE_KEY;

// Known preference keys. Each value is the property name used inside the stored JSON object.
export const DASHBOARD_PREF = Object.freeze({
  AUTO_OPEN_VIEWER: 'autoOpenViewer',
});

// Registered default for every known preference. `autoOpenViewer` defaults ON because the
// dashboard has always opened the 3D viewer on chart-filter selection - an absent or corrupt
// store must preserve that existing behavior.
const PREFERENCE_DEFAULTS = Object.freeze({
  [DASHBOARD_PREF.AUTO_OPEN_VIEWER]: true,
});

/**
 * Read the entire preferences object from localStorage. A missing key, unavailable storage,
 * unparseable JSON, or a non-object payload (array / string / null) all yield an empty object.
 * Total: never throws.
 * @returns {Object}
 */
function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    // localStorage unavailable or value corrupt - fall back to defaults
    return {};
  }
}

/**
 * Read one dashboard preference by key. Falls back to the registered default when the key is
 * unset or storage is missing / corrupt; an unknown key yields undefined. Total: never throws.
 * @param {string} key one of DASHBOARD_PREF
 * @returns {*}
 */
export function getDashboardPreference(key) {
  const all = readAll();
  if (Object.prototype.hasOwnProperty.call(all, key)) return all[key];
  return Object.prototype.hasOwnProperty.call(PREFERENCE_DEFAULTS, key)
    ? PREFERENCE_DEFAULTS[key]
    : undefined;
}

/**
 * Persist one dashboard preference by key, merging into the existing object so sibling
 * preferences survive. Unavailable or full storage is silently ignored. Total: never throws.
 * @param {string} key one of DASHBOARD_PREF
 * @param {*} value
 * @returns {void}
 */
export function setDashboardPreference(key, value) {
  try {
    const next = { ...readAll(), [key]: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    // localStorage full or unavailable - silently ignore
  }
}
